import { TrendingUp, CheckCircle, Users, Activity, Plus, X } from 'lucide-react'

export default function ActivityNotesSection({
  todayTimesheet, userStats, notes, newNote, setNewNote, addNote, toggleNote, deleteNote
}) {
  return (
    <div className="space-y-6 mt-8">
      {/* Split Layout: Statistik Hari Ini (Left) & Notes (Right) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Statistik Hari Ini - Left */}
        <div className="relative backdrop-blur-xl bg-white/70 shadow-xl rounded-3xl border border-white/40 p-6 overflow-hidden">
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer pointer-events-none"></div>

          <h3 className="relative text-lg font-bold mb-4 flex items-center gap-2 bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent">
            <TrendingUp className="w-5 h-5 text-orange-600" />
            Statistik Hari Ini
          </h3>

          <div className="space-y-3">
            {/* Attendance Streak */}
            <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 rounded-xl p-4 border border-green-100 hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="bg-green-500 p-2 rounded-lg">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-gray-700">Streak Kehadiran</span>
                </div>
                <span className="text-2xl font-bold text-green-600">7</span>
              </div>
              <p className="text-xs text-gray-600">🔥 Hari berturut-turut hadir tepat waktu!</p>
            </div>

            {/* Tasks Completed Today */}
            <div className="bg-gradient-to-br from-blue-50 via-blue-50 to-blue-50 rounded-xl p-4 border border-blue-100 hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="bg-blue-500 p-2 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-700">Tugas Selesai</span>
                </div>
                <span className="text-2xl font-bold text-blue-600">{todayTimesheet.length}</span>
              </div>
              <p className="text-xs text-gray-600">✨ Dari {todayTimesheet.length + 2} tugas yang dijadwalkan</p>
            </div>

            {/* Team Collaboration */}
            <div className="bg-gradient-to-br from-blue-50 via-pink-50 to-rose-50 rounded-xl p-4 border border-blue-100 hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="bg-blue-500 p-2 rounded-lg">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-700">Kolaborasi Tim</span>
                </div>
                <span className="text-2xl font-bold text-blue-600">{userStats.totalLoggedIn}</span>
              </div>
              <p className="text-xs text-gray-600">👥 Anggota tim sedang online sekarang</p>
            </div>

            {/* Productivity Score */}
            <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 rounded-xl p-4 border border-amber-100 hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="bg-amber-500 p-2 rounded-lg">
                    <Activity className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-700">Skor Produktivitas</span>
                </div>
                <span className="text-2xl font-bold text-amber-600">85%</span>
              </div>
              <p className="text-xs text-gray-600">📈 Meningkat 12% dari kemarin</p>
            </div>
          </div>
        </div>

        {/* Notes & Pengingat - Right */}
        <div className="relative backdrop-blur-xl bg-white/80 shadow-xl rounded-3xl p-6 border border-white/40 overflow-hidden">
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer pointer-events-none"></div>

          <h3 className="relative text-lg font-bold mb-4 flex items-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Notes & Pengingat
          </h3>

          {/* Add Note Input */}
          <div className="mb-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addNote()}
                placeholder="Tambah catatan atau pengingat..."
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={addNote}
                className="group relative bg-gradient-to-br from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700 text-white p-2.5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-110"
                title="Tambah Note"
              >
                <div className="absolute inset-0 bg-white/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <Plus className="w-5 h-5 relative z-10 group-hover:rotate-90 transition-transform duration-300" />
              </button>
            </div>
          </div>

          {/* Notes List */}
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {notes.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Belum ada catatan</p>
                <p className="text-xs">Tambahkan pengingat pekerjaan Anda di sini</p>
              </div>
            ) : (
              notes.map((note) => (
                <div
                  key={note.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${note.completed
                    ? 'bg-gray-50 border-gray-200'
                    : 'bg-blue-50 border-blue-200'
                    }`}
                >
                  <button
                    onClick={() => toggleNote(note.id)}
                    className="flex-shrink-0 mt-0.5"
                  >
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${note.completed
                        ? 'bg-green-500 border-green-500'
                        : 'border-gray-300 hover:border-blue-500'
                        }`}
                    >
                      {note.completed && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${note.completed ? 'line-through text-gray-500' : 'text-gray-700 font-medium'}`}>
                      {note.text}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(note.createdAt).toLocaleString('id-ID', {
                        hour: '2-digit',
                        minute: '2-digit',
                        day: 'numeric',
                        month: 'short'
                      })}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteNote(note.id)}
                    className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors"
                    title="Hapus"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Notes Summary */}
          {notes.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
              <span>{notes.filter(n => !n.completed).length} belum selesai</span>
              <span>{notes.filter(n => n.completed).length} selesai</span>
            </div>
          )}
        </div>
      </div>

      {/* Tips Card */}
      <div className="bg-white shadow-md rounded-2xl p-6 border border-yellow-200">
        <div className="flex items-start gap-3 mb-3">
          <div className="bg-amber-100 p-2 rounded-lg">
            <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </div>
          <div>
            <h4 className="font-bold text-gray-800 mb-1">💡 Tips Hari Ini</h4>
            <p className="text-sm text-gray-600">
              Jangan lupa untuk selalu update timesheet dan dokumentasi project Anda secara berkala!
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
